import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import { PhoneNumberUtil } from 'google-libphonenumber';
import PhoneInput from 'react-phone-input-2';
import FormControlError from '../FormControlError';
import identity from 'lodash/identity';

import 'react-phone-input-2/lib/style.css';
import './PhoneNumberField.less';

const phoneUtil = PhoneNumberUtil.getInstance();

const getPhoneNumberInfo = (/** @type {string} */ phone) => {
  let pn = null;
  try {
    pn = phoneUtil.parseAndKeepRawInput('+' + phone);
  } catch (error) { }

  return {
    possible: pn ? phoneUtil.isPossibleNumber(pn) : false,
    valid: pn ? phoneUtil.isValidNumber(pn) : false,
    reason: pn ? phoneUtil.isPossibleNumberWithReason(pn) : null,
    nn: pn?.getNationalNumber(),
    value: '+' + phone,
    hasNN: pn?.hasNationalNumber(),
    cCode: pn?.getCountryCode(),
    cCodeSourceCount: pn?.countryCodeSourceCount(),
    pn,
  };
};

/**
 *
 * @param {{
 *  title?:string;
 *  required?:boolean;
 *  disabled?:boolean;
 *  id?:string;
 *  name?:string;
 *  value?:string;
 *  placeholder?:string;
 *  onChange?(name:string, value:string, data:import('react-phone-input-2').CountryData):void;
 *  initialCountryCode?:string;
 *  minLen?:number;
 * }} props
 * @returns {React.ReactElement}
 */
export const PhoneNumberField = props => {
  const { title, required, id, name, value, onChange, placeholder, disabled, initialCountryCode, minLen = 0, } = props;
  const [errorMessage, setErrorMessage] = useState(/** @type {string=} */(void 0));
  const [isDirty, setDirty] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const inputFieldRef = useRef(/** @type {null|HTMLInputElement} */(null));

  const classNames = [
    !!errorMessage ? 'has-error' : ''
  ].filter(identity).join(' ');
  const inputClassNames = [
    'lsn-control lsn-control-phone form-input',
    !!errorMessage ? 'has-error' : ''
  ].filter(identity).join(' ');

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleFocus = e => {
    setDirty(true);
    inputFieldRef.current?.setSelectionRange(inputFieldRef.current?.value?.length ?? 0, inputFieldRef.current?.value?.length ?? 0);
  };

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleBlur = e => {
    setDirty(true);
  };

  /**
   *
   * @param {string} value
   * @param {import('react-phone-input-2').CountryData} data
   */
  const handleChange = (value, data) => {
    setDirty(true);
    setCurrentValue(value);
    // we need to override target's value with unformatted one
    onChange?.(name ?? '', value ?? '', data);
  };

  /**
   *
   * @param {string=} value
   * @returns {string}
   */
  const _getNativeErrorMessage = (value) => {
    const phInfo = getPhoneNumberInfo(value ?? '');
    const valueIsRequired = required ? !phInfo.nn : false;
    const isValid = phInfo.valid;
    const status = valueIsRequired ? 'required' : !isValid ? 'invalid' : '';
    return status ? `Phone Number is ${status}` : '';
  };

  /**
   *
   * @param {string=} value
   */
  const refreshErrorMessage = (value) => {
    const errorMessage = disabled ? '' : isDirty ? _getNativeErrorMessage(value) : '';
    setErrorMessage(errorMessage);
    // report validity to this control
    errorMessage
      ? inputFieldRef.current?.setCustomValidity(typeof errorMessage === 'string' ? errorMessage : 'This field is invalid')
      : inputFieldRef.current?.setCustomValidity('');
    return !!errorMessage;
  };

  useEffect(() => {
    const flagEl = document.querySelector(`.selected-flag`);
    if (!currentValue) {
      flagEl?.setAttribute('title', 'Select Phone');
    }
  }, [currentValue]);

  // refresh value
  useEffect(() => {
    setCurrentValue(value ?? '');
    setDirty(!!value || isDirty);
  }, [value]);

  // update the vaidity when control gets dirty or the value is being changed
  useEffect(() => {
    refreshErrorMessage(currentValue ?? '');
  }, [isDirty, currentValue, required, disabled]);

  return (<>
    <PhoneInput
      inputProps={{
        id,
        name,
        title,
        required,
        pattern: (currentValue?.length || 0) < minLen || errorMessage ? '_some_invalid_pattern_' : void 0,
        ref: inputFieldRef,
        onInvalid: handleBlur,
        onBlur: handleBlur,
        onFocus: handleFocus,
      }}
      country={initialCountryCode}
      containerClass={classNames}
      inputClass={inputClassNames}
      value={currentValue}
      placeholder={placeholder}
      disabled={disabled}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onChange={handleChange}
      isValid={refreshErrorMessage}
    />
    {required ? <div className='control-error-wrapper'>
      <FormControlError show={!!errorMessage} text={errorMessage} />
    </div> : null}
  </>
  );
};

export default PhoneNumberField;
